import React, { useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { companiesService } from '../services/companies';
import { useFetch } from '../hooks/useFetch';
import Spinner from '../components/Spinner';
import StatusMessage from '../components/StatusMessage';
import Modal from '../components/Modal';
import Tabs from '../components/Tabs';
import DataTable, { Column } from '../components/DataTable';
import BarChart30 from '../components/BarChart30';
import TrialQuotaModal from './companies/TrialQuotaModal';
import type { AdminStationRow, AdminEmployeeRow, AdminCompanyOverview } from '../types';

const STATUS_LABELS: Record<string, string> = {
  present: 'Hadir',
  late: 'Telat',
  early_leave: 'Pulang cepat',
  late_and_early: 'Telat & pulang cepat',
  absent: 'Alpa',
  off: 'Libur',
};

const INVOICE_TYPE_LABELS: Record<string, string> = {
  renewal: 'Perpanjangan',
  quota_addition: 'Tambah Kuota',
};

const DAY_NAMES: Record<number, string> = {
  1: 'Sen',
  2: 'Sel',
  3: 'Rab',
  4: 'Kam',
  5: 'Jum',
  6: 'Sab',
  7: 'Min',
};

function fmtRupiah(amount: number): string {
  return `Rp ${amount.toLocaleString('id-ID')}`;
}

export function fmtTanggal(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
}

type TabKey = 'stations' | 'users' | 'activity' | 'billing' | 'features' | 'audit';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'stations', label: 'Titik Absen' },
  { key: 'users', label: 'Pegawai' },
  { key: 'activity', label: 'Aktivitas' },
  { key: 'billing', label: 'Langganan' },
  { key: 'features', label: 'Fitur & Setelan' },
  { key: 'audit', label: 'Audit' },
];

const Badge = ({ text, tone = 'gray' }: { text: string; tone?: 'gray' | 'red' | 'amber' }) => {
  const toneClass = {
    gray: 'bg-gray-200 text-gray-600',
    red: 'bg-red-100 text-red-700',
    amber: 'bg-amber-100 text-amber-700',
  }[tone];
  return <span className={`px-2 py-0.5 rounded text-xs ${toneClass}`}>{text}</span>;
};

const StatTile = ({ label, value, valueClassName }: { label: string; value: React.ReactNode; valueClassName?: string }) => (
  <div className="bg-white border rounded-lg p-4">
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-lg font-semibold mt-1 ${valueClassName ?? ''}`}>{value}</div>
  </div>
);

const CompanyDetail = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const flash = (location.state as { flash?: string } | null)?.flash ?? null;
  const { data, loading, error, reload } = useFetch(() => companiesService.get(id), [id]);
  const { data: overview, error: overviewError } = useFetch(() => companiesService.overview(id), [id]);

  const [showSuspend, setShowSuspend] = useState(false);
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [showTrial, setShowTrial] = useState(false);

  const [activeTab, setActiveTab] = useState<TabKey>('stations');
  const [stationsOpened, setStationsOpened] = useState(true);
  const [usersOpened, setUsersOpened] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const handleTabChange = (key: string) => {
    const k = key as TabKey;
    setActiveTab(k);
    if (k === 'stations') setStationsOpened(true);
    if (k === 'users') setUsersOpened(true);
  };

  const stationsFetch = useFetch(
    () => (stationsOpened ? companiesService.stations(id) : Promise.resolve([] as AdminStationRow[])),
    [id, stationsOpened],
  );
  const usersFetch = useFetch(
    () =>
      usersOpened
        ? companiesService.usersOf(id, { search, page })
        : Promise.resolve({ data: [] as AdminEmployeeRow[], meta: undefined }),
    [id, usersOpened, search, page],
  );

  const doSuspend = async () => {
    setMsg(null);
    try {
      await companiesService.suspend(id, reason);
      setShowSuspend(false);
      setReason('');
      reload();
    } catch (e) {
      setMsg((e as Error).message);
    }
  };
  const doReactivate = async () => {
    setMsg(null);
    try {
      await companiesService.reactivate(id);
      reload();
    } catch (e) {
      setMsg((e as Error).message);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setSearch(searchInput);
      setPage(1);
    }
  };

  if (loading) return <Spinner />;
  if (error) return <StatusMessage error={error} />;
  if (!data) return null;

  const stats = overview?.stats;
  const usersMeta = usersFetch.data?.meta;

  const stationColumns: Column<AdminStationRow>[] = [
    { key: 'name', header: 'Nama' },
    { key: 'address', header: 'Alamat', render: (r) => r.address ?? '—' },
    { key: 'distance_limit', header: 'Radius (m)' },
    { key: 'jam', header: 'Jam', render: (r) => `${r.clock_in_time ?? '—'}–${r.clock_out_time ?? '—'}` },
    { key: 'shift', header: 'Shift', render: (r) => (r.uses_shift ? r.shift_count : '—') },
    { key: 'employee_count', header: 'Pegawai' },
    { key: 'attendance_total', header: 'Absen total' },
    {
      key: 'attendance_30d',
      header: 'Absen 30 hari',
      render: (r) => (r.attendance_30d === 0 ? <Badge text="Tidak aktif" tone="red" /> : r.attendance_30d),
    },
    { key: 'last_attendance_at', header: 'Terakhir dipakai', render: (r) => fmtTanggal(r.last_attendance_at) },
  ];

  const userColumns: Column<AdminEmployeeRow>[] = [
    {
      key: 'full_name',
      header: 'Nama',
      render: (r) => (
        <>
          {r.full_name}
          {!r.is_active && (
            <span className="ml-2">
              <Badge text="Nonaktif" tone="gray" />
            </span>
          )}
        </>
      ),
    },
    {
      key: 'kontak',
      header: 'Kontak',
      render: (r) => (
        <div>
          <div>{r.email ?? '—'}</div>
          <div>{r.phone ?? '—'}</div>
        </div>
      ),
    },
    { key: 'job_title', header: 'Jabatan', render: (r) => r.job_title ?? '—' },
    { key: 'role', header: 'Role', render: (r) => `${r.user_role ?? '—'} / ${r.company_role ?? '—'}` },
    { key: 'station_name', header: 'Titik absen', render: (r) => r.station_name ?? '—' },
    { key: 'attendance_30d', header: 'Absen 30 hari' },
    { key: 'last_attendance_at', header: 'Terakhir absen', render: (r) => fmtTanggal(r.last_attendance_at) },
  ];

  const invoiceColumns: Column<AdminCompanyOverview['invoices'][number]>[] = [
    { key: 'created_at', header: 'Tanggal', render: (r) => fmtTanggal(r.created_at) },
    { key: 'type', header: 'Tipe', render: (r) => INVOICE_TYPE_LABELS[r.type] ?? r.type },
    { key: 'months', header: 'Durasi', render: (r) => (r.months ? `${r.months} bulan` : '—') },
    { key: 'amount', header: 'Nominal', render: (r) => fmtRupiah(r.amount) },
    { key: 'status', header: 'Status' },
    { key: 'paid_at', header: 'Dibayar', render: (r) => fmtTanggal(r.paid_at) },
  ];

  return (
    <div className="space-y-5">
      <button className="text-sm text-gray-500" onClick={() => navigate('/companies')}>← Companies</button>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{data.company_name}</h1>
        {data.is_suspended ? (
          <button onClick={doReactivate} className="bg-primary-600 text-white rounded px-4 py-2 text-sm">Reactivate</button>
        ) : (
          <button onClick={() => setShowSuspend(true)} className="bg-red-600 text-white rounded px-4 py-2 text-sm">Suspend</button>
        )}
      </div>
      {flash && <div className="rounded bg-green-50 text-green-700 px-4 py-3 text-sm">{flash}</div>}
      {msg && <StatusMessage error={msg} />}
      <div className="bg-white border rounded-lg p-4 text-sm space-y-1 max-w-2xl">
        <div>Code: {data.company_code ?? '—'}</div>
        <div>Business type: {data.business_type ?? '—'}</div>
        <div>Phone: {data.company_phone ?? '—'}</div>
        <div>Address: {data.address ?? '—'}</div>
        <div>Employees: {data.employee_count}</div>
        <div>
          Subscription: <b>{data.subscription_status}</b>
          {data.subscription?.valid_until ? ` · valid until ${data.subscription.valid_until}` : ''}
          {data.subscription?.is_trial && (
            <span className="ml-2">
              <Badge text="TRIAL" tone="amber" />
            </span>
          )}
        </div>
        {data.subscription?.is_trial && (
          <div className="flex items-center gap-3">
            <span>
              Kuota trial: {data.subscription.station_quota} titik · {data.subscription.addon_user_quota} pegawai tambahan · berakhir{' '}
              {fmtTanggal(data.subscription.valid_until)}
            </span>
            <button onClick={() => setShowTrial(true)} className="text-primary-600 text-sm">Ubah kuota trial</button>
          </div>
        )}
        <div>Suspended: {data.is_suspended ? 'Yes' : 'No'}</div>
      </div>
      <div className="bg-white border rounded-lg p-4 text-sm max-w-2xl">
        <div className="font-semibold mb-1">Branches</div>
        {data.branches.length === 0 ? (
          <div className="text-gray-500">None</div>
        ) : (
          <ul className="list-disc pl-5">
            {data.branches.map((b) => <li key={b.id}>{b.name ?? b.id}</li>)}
          </ul>
        )}
      </div>

      {overviewError && <StatusMessage error={overviewError} />}

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatTile label="Pegawai aktif" value={stats.active_employees} />
          <StatTile
            label="Titik absen"
            value={`${stats.station_count}/${stats.station_quota}`}
            valueClassName={stats.station_count >= stats.station_quota ? 'text-red-600' : ''}
          />
          <StatTile
            label="Kursi pegawai"
            value={`${stats.occupied_seats}/${stats.user_quota}`}
            valueClassName={stats.occupied_seats >= stats.user_quota ? 'text-red-600' : ''}
          />
          <StatTile label="Absen 30 hari" value={stats.attendance_30d} />
          <StatTile
            label="Langganan"
            value={
              <>
                {stats.subscription_status} · {stats.days_remaining} hari
                {stats.is_trial && (
                  <span className="ml-2">
                    <Badge text="TRIAL" tone="amber" />
                  </span>
                )}
              </>
            }
          />
          <StatTile
            label="Onboarding"
            value={
              stats.onboarding_completed_at ? (
                fmtTanggal(stats.onboarding_completed_at)
              ) : (
                <span className="text-amber-600">Belum</span>
              )
            }
          />
        </div>
      )}

      <Tabs tabs={TABS} active={activeTab} onChange={handleTabChange} />

      <div>
        {activeTab === 'stations' &&
          (stationsFetch.loading ? (
            <Spinner />
          ) : stationsFetch.error ? (
            <StatusMessage error={stationsFetch.error} />
          ) : (
            <DataTable<AdminStationRow> columns={stationColumns} rows={stationsFetch.data ?? []} />
          ))}

        {activeTab === 'users' && (
          <div className="space-y-3">
            <input
              className="border rounded px-3 py-2 text-sm"
              placeholder="Cari nama/kontak…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            {overview && overview.pending_invitations.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <div className="font-semibold mb-1">Undangan pending</div>
                <ul className="space-y-1">
                  {overview.pending_invitations.map((inv, i) => (
                    <li key={i}>
                      {inv.full_name ?? '—'} · {inv.phone ?? '—'} · {fmtTanggal(inv.created_at)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {usersFetch.loading ? (
              <Spinner />
            ) : usersFetch.error ? (
              <StatusMessage error={usersFetch.error} />
            ) : (
              <>
                <DataTable<AdminEmployeeRow> columns={userColumns} rows={usersFetch.data?.data ?? []} />
                <div className="flex items-center gap-2 text-sm">
                  <button
                    disabled={!usersMeta || (usersMeta.current_page ?? 1) <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="border rounded px-3 py-1 disabled:opacity-40"
                  >
                    Sebelumnya
                  </button>
                  <span>
                    Halaman {usersMeta?.current_page ?? 1} / {usersMeta?.last_page ?? 1}
                  </span>
                  <button
                    disabled={!usersMeta || (usersMeta.current_page ?? 1) >= (usersMeta.last_page ?? 1)}
                    onClick={() => setPage((p) => p + 1)}
                    className="border rounded px-3 py-1 disabled:opacity-40"
                  >
                    Berikutnya
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'activity' &&
          (overview ? (
            <div className="space-y-4">
              <BarChart30 data={overview.activity.daily} />
              <div className="flex flex-wrap gap-2">
                {Object.entries(overview.activity.status_30d).map(([status, count]) => (
                  <Badge key={status} text={`${STATUS_LABELS[status] ?? status} ${count}`} />
                ))}
              </div>
            </div>
          ) : overviewError ? (
            <StatusMessage error={overviewError} />
          ) : (
            <Spinner />
          ))}

        {activeTab === 'billing' &&
          (overview ? (
            <div className="space-y-3">
              <DataTable<AdminCompanyOverview['invoices'][number]>
                columns={invoiceColumns}
                rows={overview.invoices}
              />
              <Link to="/subscriptions" className="text-sm text-primary-600">Kelola tagihan →</Link>
            </div>
          ) : overviewError ? (
            <StatusMessage error={overviewError} />
          ) : (
            <Spinner />
          ))}

        {activeTab === 'features' &&
          (overview ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                <StatTile label="Task total" value={overview.adoption.tasks_total} />
                <StatTile label="Task 30 hari" value={overview.adoption.tasks_30d} />
                <StatTile label="Grup V-Log" value={overview.adoption.video_log_groups} />
                <StatTile label="Log V-Log 30 hari" value={overview.adoption.video_logs_30d} />
                <StatTile label="Slip gaji total" value={overview.adoption.payslips_total} />
                <StatTile label="Slip gaji terakhir" value={fmtTanggal(overview.adoption.last_payslip_at)} />
                <StatTile label="Benefit aktif" value={overview.adoption.benefits_active} />
                <StatTile label="Koreksi 30 hari" value={overview.adoption.corrections_30d} />
                <StatTile label="Lembur 30 hari" value={overview.adoption.overtime_30d} />
                <StatTile label="Cuti 30 hari" value={overview.adoption.vacations_30d} />
              </div>
              <div className="bg-white border rounded-lg p-4 text-sm space-y-2 max-w-2xl">
                <div className="font-semibold">Setelan</div>
                <div>Timezone: {overview.settings.timezone}</div>
                <div>
                  Hari kerja:{' '}
                  {overview.settings.workdays.map((d) => DAY_NAMES[d] ?? d).join(', ')}
                </div>
                <div>Hari libur: {overview.settings.holiday_count}</div>
                <div>
                  <div className="mb-1">Role:</div>
                  {overview.settings.roles.length === 0 ? (
                    <div className="text-gray-500">—</div>
                  ) : (
                    <ul className="list-disc pl-5">
                      {overview.settings.roles.map((r) => (
                        <li key={r.name}>{r.name} — {r.member_count} anggota</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ) : overviewError ? (
            <StatusMessage error={overviewError} />
          ) : (
            <Spinner />
          ))}

        {activeTab === 'audit' &&
          (overview ? (
            overview.suspensions.length === 0 ? (
              <div className="text-gray-500 text-sm py-6">Belum ada riwayat.</div>
            ) : (
              <ul className="space-y-2">
                {overview.suspensions.map((s, i) => (
                  <li key={i} className="bg-white border rounded-lg p-3 text-sm flex items-center justify-between">
                    <div>
                      <div>{fmtTanggal(s.suspended_at)} · <span>{s.reason ?? '—'}</span></div>
                      {s.suspended_by_name && (
                        <div className="text-gray-500 text-xs">oleh {s.suspended_by_name}</div>
                      )}
                    </div>
                    {s.is_suspended ? (
                      <Badge text="Suspend" tone="red" />
                    ) : (
                      <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">Reaktivasi</span>
                    )}
                  </li>
                ))}
              </ul>
            )
          ) : overviewError ? (
            <StatusMessage error={overviewError} />
          ) : (
            <Spinner />
          ))}
      </div>

      {data.subscription?.is_trial && (
        <TrialQuotaModal
          open={showTrial}
          onClose={() => setShowTrial(false)}
          companyId={id}
          subscription={data.subscription}
          onSaved={() => {
            setShowTrial(false);
            reload();
          }}
        />
      )}

      <Modal open={showSuspend} title="Suspend company" onClose={() => setShowSuspend(false)}>
        <div className="space-y-3">
          <textarea
            className="w-full border rounded px-3 py-2 text-sm"
            placeholder="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <button onClick={doSuspend} className="bg-red-600 text-white rounded px-4 py-2 text-sm w-full">Confirm suspend</button>
        </div>
      </Modal>
    </div>
  );
};
export default CompanyDetail;
